import { useCallback, useEffect, useState } from 'react'
import { AlertCircle, BookOpen, CheckCircle2, ClipboardList, Download, FileDown, FileText, RefreshCw, Users, Trophy } from 'lucide-react'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { jsPDF } from 'jspdf'
import { apiGet } from '../../services/api'
import LoadingSpinner from '../../components/common/LoadingSpinner'

const CHART_COLOR = '#4f46e5'

function downloadCsv(filename: string, rows: string[][]) {
  const csv = rows.map((row) => row.map((value) => `"${value.replace(/"/g, '""')}"`).join(',')).join('\r\n')
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

function addPdfTrendChart(doc: jsPDF, trend: LearningAnalytics['questionnaire_results']['score_trend']) {
  const x = 14
  const y = 58
  const width = 180
  const height = 48
  const chartPoints = trend.filter((point) => point.average_score !== null)
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  doc.setTextColor(31, 41, 55)
  doc.text('Average score trend', x, y - 4)
  doc.setDrawColor(203, 213, 225)
  doc.setLineWidth(0.2)
  doc.rect(x, y, width, height)
  for (const score of [0, 50, 100]) {
    const lineY = y + height - (score / 100) * height
    doc.setDrawColor(226, 232, 240)
    doc.line(x, lineY, x + width, lineY)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(7)
    doc.setTextColor(100, 116, 139)
    doc.text(`${score}%`, x - 1, lineY - 1, { align: 'right' })
  }
  if (chartPoints.length === 0) {
    doc.setFontSize(8)
    doc.text('No historical score trend is available yet.', x + 5, y + height / 2)
    return
  }
  doc.setDrawColor(79, 70, 229)
  doc.setFillColor(79, 70, 229)
  doc.setLineWidth(0.8)
  const coordinates = chartPoints.map((point, index) => ({
    x: x + (chartPoints.length === 1 ? width / 2 : (index / (chartPoints.length - 1)) * width),
    y: y + height - ((point.average_score || 0) / 100) * height,
  }))
  for (let index = 1; index < coordinates.length; index += 1) {
    doc.line(coordinates[index - 1].x, coordinates[index - 1].y, coordinates[index].x, coordinates[index].y)
  }
  coordinates.forEach((point) => doc.circle(point.x, point.y, 1.2, 'F'))
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(6)
  doc.setTextColor(71, 85, 105)
  doc.text(chartPoints[0].date, x, y + height + 4)
  if (chartPoints.length > 1) {
    doc.text(chartPoints[chartPoints.length - 1].date, x + width, y + height + 4, { align: 'right' })
  }
}

function addPdfTable(doc: jsPDF, rows: string[][], startY: number) {
  const margin = 14
  const rowHeight = 8
  const pageWidth = doc.internal.pageSize.getWidth()
  const widths = [58, 37, 25, 25, 37]
  let y = startY

  const drawHeader = () => {
    doc.setFillColor(238, 242, 255)
    doc.rect(margin, y, pageWidth - margin * 2, rowHeight, 'F')
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(8)
    let x = margin + 2
    ;['Questionnaire', 'Employee', 'Score', 'Correct', 'Completed'].forEach((label, index) => {
      doc.text(label, x, y + 5.5)
      x += widths[index]
    })
    y += rowHeight
  }

  drawHeader()
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(7)
  rows.forEach((row) => {
    if (y > doc.internal.pageSize.getHeight() - 18) {
      doc.addPage()
      y = 14
      drawHeader()
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(7)
    }
    let x = margin + 2
    row.forEach((cell, index) => {
      const maxWidth = widths[index] - 3
      const text = doc.splitTextToSize(cell, maxWidth)[0] || ''
      doc.text(text, x, y + 5.5)
      x += widths[index]
    })
    doc.setDrawColor(229, 231, 235)
    doc.line(margin, y + rowHeight, pageWidth - margin, y + rowHeight)
    y += rowHeight
  })
}

function makeExportRows(attempts: LearningAnalytics['questionnaire_results']['recent_attempts']) {
  return attempts.map((attempt) => [
    attempt.title || `Questionnaire #${attempt.questionnaire_id}`,
    attempt.user_name || '—',
    attempt.percentage === null ? '—' : `${attempt.percentage}%`,
    attempt.correct === null ? '—' : `${attempt.correct}${attempt.incorrect === null ? '' : ` correct / ${attempt.incorrect} incorrect`}`,
    attempt.completed_at ? new Date(attempt.completed_at).toLocaleString() : '—',
  ])
}

function exportAnalyticsPdf(analytics: LearningAnalytics) {
  const doc = new jsPDF({ orientation: 'landscape' })
  const result = analytics.questionnaire_results
  doc.setFillColor(30, 41, 59)
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 35, 'F')
  doc.setTextColor(255, 255, 255)
  doc.setFontSize(18)
  doc.text('Learning Analytics Report', 14, 16)
  doc.setFontSize(9)
  doc.text(`Generated ${new Date(analytics.generated_at || Date.now()).toLocaleString()}`, 14, 26)
  doc.setTextColor(31, 41, 55)
  doc.setFontSize(11)
  doc.text(`Participants: ${analytics.participants}    Articles: ${analytics.content.knowledge_base_articles}    Questionnaires: ${analytics.content.questionnaires}    Attempts: ${result.attempts}    Completed: ${result.completed}    Average score: ${result.average_score === null ? '—' : `${result.average_score}%`}`, 14, 46)
  addPdfTrendChart(doc, result.score_trend)
  const tableStartY = 119
  if (!result.recent_attempts.length) {
    doc.setFontSize(11)
    doc.text('No completed questionnaire results are recorded yet.', 14, tableStartY)
    doc.setFontSize(9)
    doc.text('Historical attempts completed before analytics recording was enabled may not be included.', 14, tableStartY + 10)
  } else {
    addPdfTable(doc, makeExportRows(result.recent_attempts), tableStartY)
  }
  doc.save('learning-analytics-report.pdf')
}

async function exportAnalyticsPowerPoint(analytics: LearningAnalytics) {
  const { default: PptxGenJS } = await import('pptxgenjs')
  const pptx = new PptxGenJS()
  pptx.layout = 'LAYOUT_WIDE'
  pptx.author = 'AEON'
  pptx.subject = 'Company learning analytics'
  pptx.title = 'Learning Analytics Report'
  pptx.company = 'AEON'
  pptx.theme = { headFontFace: 'Aptos Display', bodyFontFace: 'Aptos' }
  const result = analytics.questionnaire_results
  const addHeader = (slide: ReturnType<typeof pptx.addSlide>, title: string, subtitle: string) => {
    slide.background = { color: 'F8FAFC' }
    slide.addText(title, { x: 0.55, y: 0.35, w: 12.2, h: 0.45, fontFace: 'Aptos Display', fontSize: 24, bold: true, color: '1E293B' })
    slide.addText(subtitle, { x: 0.58, y: 0.9, w: 12.0, h: 0.3, fontSize: 10, color: '64748B' })
  }

  const overview = pptx.addSlide()
  addHeader(overview, 'Learning Analytics', `Generated ${new Date(analytics.generated_at || Date.now()).toLocaleString()}`)
  const metrics = [
    ['Participants', analytics.participants],
    ['Knowledge articles', analytics.content.knowledge_base_articles],
    ['Questionnaires', analytics.content.questionnaires],
    ['Attempts', result.attempts],
    ['Completed', result.completed],
    ['Average score', result.average_score === null ? '—' : `${result.average_score}%`],
  ]
  metrics.forEach(([label, value], index) => {
    const col = index % 3
    const row = Math.floor(index / 3)
    const x = 0.6 + col * 4.1
    const y = 1.55 + row * 1.45
    overview.addShape(pptx.ShapeType.roundRect, { x, y, w: 3.7, h: 1.05, rectRadius: 0.08, fill: { color: 'FFFFFF' }, line: { color: 'E2E8F0' }, shadow: { type: 'outer', color: 'CBD5E1', blur: 1, angle: 45, offset: 1, opacity: 0.15 } })
    overview.addText(String(value), { x: x + 0.2, y: y + 0.12, w: 3.3, h: 0.42, fontSize: 22, bold: true, color: '312E81' })
    overview.addText(String(label), { x: x + 0.2, y: y + 0.62, w: 3.3, h: 0.22, fontSize: 10, color: '64748B' })
  })
  overview.addText(result.completion_rate === null ? 'Completion rate unavailable until attempts are recorded.' : `Completion rate: ${result.completion_rate}%`, { x: 0.65, y: 4.75, w: 11.8, h: 0.35, fontSize: 12, color: '334155' })
  const scoredTrend = result.score_trend.filter((point) => point.average_score !== null)
  if (scoredTrend.length) {
    overview.addChart(pptx.ChartType.line, [{ name: 'Average score', labels: scoredTrend.map((point) => point.date), values: scoredTrend.map((point) => point.average_score as number) }], {
      x: 0.7, y: 5.1, w: 11.9, h: 1.6, showLegend: false, showTitle: false, showValue: true,
      catAxisLabelFontSize: 9, valAxisMinVal: 0, valAxisMaxVal: 100,
      chartColors: [CHART_COLOR.replace('#', '')],
    })
  } else {
    overview.addText('Score trend will appear as attempts are recorded.', { x: 0.65, y: 5.2, w: 11.8, h: 0.35, fontSize: 11, italic: true, color: '64748B' })
  }

  const resultsSlide = pptx.addSlide()
  addHeader(resultsSlide, 'Recent questionnaire results', 'Latest recorded attempts for this company')
  if (result.recent_attempts.length) {
    resultsSlide.addTable([
      [{ text: 'Questionnaire', options: { bold: true } }, { text: 'Employee', options: { bold: true } }, { text: 'Score', options: { bold: true } }, { text: 'Correct / incorrect', options: { bold: true } }, { text: 'Completed', options: { bold: true } }],
      ...makeExportRows(result.recent_attempts).map((row) => row.map((text) => ({ text }))),
    ], { x: 0.55, y: 1.45, w: 12.2, h: 5.3, border: { type: 'solid', color: 'E2E8F0', pt: 0.5 }, fill: { color: 'FFFFFF' }, color: '334155', fontSize: 9, rowH: 0.35, margin: 0.08, valign: 'middle' })
  } else {
    resultsSlide.addText('No completed questionnaire results are recorded yet. Historical completions before analytics recording was enabled are not backfilled.', { x: 0.7, y: 1.6, w: 11.5, h: 0.7, fontSize: 14, color: '64748B', breakLine: false })
  }
  const file = await pptx.write({ outputType: 'blob', compression: true }) as Blob
  const url = URL.createObjectURL(file)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = 'learning-analytics-report.pptx'
  anchor.click()
  URL.revokeObjectURL(url)
}

interface LearningAnalytics {
  generated_at: string
  participants: number
  content: {
    knowledge_base_articles: number
    questionnaires: number
  }
  questionnaire_results: {
    attempts: number
    completed: number
    completion_rate: number | null
    average_score: number | null
    score_distribution: Array<{ range: string; attempts: number }>
    score_trend: Array<{ date: string; average_score: number | null; attempts: number }>
    recent_attempts: Array<{
      attempt_id: number
      questionnaire_id: number
      title: string
      user_name: string
      completed_at: string | null
      percentage: number | null
      correct: number | null
      incorrect: number | null
      answered: number | null
      total: number | null
    }>
  }
  note?: string
}

const EMPTY_ANALYTICS: LearningAnalytics = {
  generated_at: '',
  participants: 0,
  content: { knowledge_base_articles: 0, questionnaires: 0 },
  questionnaire_results: {
    attempts: 0,
    completed: 0,
    completion_rate: null,
    average_score: null,
    score_distribution: [],
    score_trend: [],
    recent_attempts: [],
  },
}

function MetricCard({ title, value, detail, icon: Icon, color }: {
  title: string
  value: string | number
  detail: string
  icon: typeof BookOpen
  color: string
}) {
  return (
    <article className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{value}</p>
          <p className="mt-1 text-xs text-gray-500">{detail}</p>
        </div>
        <span className={`rounded-lg p-3 ${color}`}><Icon className="h-5 w-5" /></span>
      </div>
    </article>
  )
}

export default function LearningAnalyticsPage() {
  const [analytics, setAnalytics] = useState<LearningAnalytics>(EMPTY_ANALYTICS)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')
  const [exportError, setExportError] = useState('')

  const handlePowerPointExport = async () => {
    setExporting(true)
    setExportError('')
    try {
      await exportAnalyticsPowerPoint(analytics)
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'PowerPoint export failed. Please try again.')
    } finally {
      setExporting(false)
    }
  }

  const loadAnalytics = useCallback(async (background = false) => {
    if (background) setRefreshing(true)
    else setLoading(true)
    setError('')
    try {
      setAnalytics(await apiGet<LearningAnalytics>('/eagle-vision/learning/analytics'))
    } catch (err) {
      const error = err as {
        message?: string
        response?: { status?: number; data?: { message?: string; error?: string } }
      }
      const responseMessage = error?.response?.data?.message
      const status = error?.response?.status
      const detail = status === 403 && responseMessage === 'Permission denied'
        ? 'Access denied. In Superadmin → Companies → Role Permissions, enable Admin Pages → Learning Analytics → View for your role.'
        : responseMessage || error?.message || 'Check the backend connection and server logs.'
      setError(status ? `${detail} (HTTP ${status})` : detail)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => { void loadAnalytics() }, [loadAnalytics])

  if (loading) return <div className="flex h-64 items-center justify-center"><LoadingSpinner size="lg" /></div>

  const result = analytics.questionnaire_results
  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-sm text-gray-500">Learning program overview</p>
          <h1 className="text-2xl font-bold text-gray-900">Knowledge Base & Questionnaire Analytics</h1>
          <p className="mt-1 text-sm text-gray-500">Assigned content and questionnaire results for your company.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => exportAnalyticsPdf(analytics)} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
            <FileDown className="h-4 w-4" /> PDF
          </button>
          <button onClick={() => void handlePowerPointExport()} disabled={loading || exporting} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
            <Download className="h-4 w-4" /> {exporting ? 'Exporting…' : 'PowerPoint'}
          </button>
          <button onClick={() => downloadCsv('learning-questionnaire-results.csv', [['Questionnaire', 'Employee', 'Score', 'Correct / incorrect', 'Completed'], ...makeExportRows(analytics.questionnaire_results.recent_attempts)])} disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
            <FileText className="h-4 w-4" /> CSV
          </button>
          <button onClick={() => void loadAnalytics(true)} disabled={refreshing} className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60">
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </header>

      {error && <div role="alert" className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"><AlertCircle className="h-4 w-4 shrink-0" />{error}</div>}
      {exportError && <div role="alert" className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"><AlertCircle className="h-4 w-4 shrink-0" />{exportError}</div>}
      {analytics.note && <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">{analytics.note}</div>}

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <MetricCard title="Learning participants" value={analytics.participants} detail="Active, linked employees with Learning access" icon={Users} color="bg-indigo-50 text-indigo-700" />
        <MetricCard title="Knowledge Base articles" value={analytics.content.knowledge_base_articles} detail="Unique articles assigned across eligible employees" icon={BookOpen} color="bg-blue-50 text-blue-700" />
        <MetricCard title="Questionnaires" value={analytics.content.questionnaires} detail="Unique questionnaires assigned across eligible employees" icon={ClipboardList} color="bg-violet-50 text-violet-700" />
        <MetricCard title="Questionnaire attempts" value={result.attempts} detail={`${result.completed} completed`} icon={CheckCircle2} color="bg-green-50 text-green-700" />
        <MetricCard title="Average quiz score" value={result.average_score === null ? '—' : `${result.average_score}%`} detail={result.completion_rate === null ? 'No completion data available' : `${result.completion_rate}% attempt completion`} icon={Trophy} color="bg-amber-50 text-amber-700" />
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="font-semibold text-gray-900">Questionnaire score trend</h2>
            <p className="mt-1 text-xs text-gray-500">Daily average score from completed, recorded attempts.</p>
          </div>
          <span className="rounded-lg bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-700">{result.score_trend.reduce((sum, point) => sum + point.attempts, 0)} recorded</span>
        </div>
        {result.score_trend.length ? <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={result.score_trend} margin={{ top: 8, right: 16, left: 4, bottom: 4 }}>
            <defs><linearGradient id="learningScoreFill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={CHART_COLOR} stopOpacity={0.28} /><stop offset="95%" stopColor={CHART_COLOR} stopOpacity={0.02} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} unit="%" />
            <Tooltip formatter={(value, name) => name === 'Average score' ? [`${value}%`, name] : [value, name]} />
            <Area type="monotone" dataKey="average_score" name="Average score" stroke={CHART_COLOR} strokeWidth={3} fill="url(#learningScoreFill)" connectNulls dot={{ r: 4, fill: CHART_COLOR }} activeDot={{ r: 6 }} />
          </AreaChart>
        </ResponsiveContainer> : <p className="rounded-lg bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">No historical score series is available yet. Scores will appear as employees complete questionnaires through this app.</p>}
      </section>

      <section className="grid grid-cols-1 gap-6 xl:grid-cols-5">
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm xl:col-span-2">
          <div className="mb-5 flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-600" />
            <div>
              <h2 className="font-semibold text-gray-900">Questionnaire score distribution</h2>
              <p className="text-xs text-gray-500">Completed attempts</p>
            </div>
          </div>
          {result.score_distribution.length ? <div className="space-y-4">
            {result.score_distribution.map((bucket) => {
              const max = Math.max(...result.score_distribution.map((entry) => entry.attempts), 1)
              return <div key={bucket.range}>
                <div className="mb-1 flex justify-between text-sm"><span className="text-gray-600">{bucket.range}</span><span className="font-medium text-gray-900">{bucket.attempts}</span></div>
                <div className="h-2 overflow-hidden rounded-full bg-gray-100"><div className="h-full rounded-full bg-indigo-500" style={{ width: `${bucket.attempts / max * 100}%` }} /></div>
              </div>
            })}
          </div> : <p className="rounded-lg bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">No completed quiz scores recorded yet.</p>}
        </div>

        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm xl:col-span-3">
          <div className="border-b border-gray-100 p-5">
            <h2 className="font-semibold text-gray-900">Recent questionnaire results</h2>
            <p className="mt-1 text-xs text-gray-500">Latest completed attempts</p>
          </div>
          {result.recent_attempts.length ? <div className="overflow-x-auto">
            <table className="w-full min-w-[650px] text-left text-sm">
              <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500"><tr>
                <th className="px-4 py-3 font-medium">Questionnaire</th><th className="px-4 py-3 font-medium">Employee</th><th className="px-4 py-3 font-medium">Score</th><th className="px-4 py-3 font-medium">Correct</th><th className="px-4 py-3 font-medium">Completed</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-100">
                {result.recent_attempts.map((attempt) => <tr key={attempt.attempt_id}>
                  <td className="px-4 py-3 font-medium text-gray-900">{attempt.title || `Questionnaire #${attempt.questionnaire_id}`}</td>
                  <td className="px-4 py-3 text-gray-600">{attempt.user_name || '—'}</td>
                  <td className="px-4 py-3 font-semibold text-gray-900">{attempt.percentage === null ? '—' : `${attempt.percentage}%`}</td>
                  <td className="px-4 py-3 text-gray-600">{attempt.correct === null ? '—' : `${attempt.correct}${attempt.incorrect === null ? '' : ` / ${attempt.incorrect} wrong`}`}</td>
                  <td className="px-4 py-3 text-gray-500">{attempt.completed_at ? new Date(attempt.completed_at).toLocaleString() : '—'}</td>
                </tr>)}
              </tbody>
            </table>
          </div> : <div className="px-5 py-10 text-center text-sm text-gray-500">No completed questionnaire results are available yet.</div>}
        </div>
      </section>
      {analytics.generated_at && <p className="text-right text-xs text-gray-400">Updated {new Date(analytics.generated_at).toLocaleString()}</p>}
    </div>
  )
}
